import type { FaixaDeImpersonacao } from '@levitas/contracts';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { api } from '../../lib/api';
import { useSessao } from '../../lib/usar-sessao';

/**
 * A faixa fixa do acesso de suporte (M17, cenário 18).
 *
 * Ela é **fixa** de propósito: enquanto alguém da El Shaday estiver dentro da
 * conta, ninguém — nem o suporte, nem quem estiver olhando a tela por cima do
 * ombro dele — pode esquecer disso. Ela diz em qual igreja o acesso está, por
 * quê, e até quando.
 *
 * Some sozinha quando a janela acaba: o servidor recusa, a consulta falha, e a
 * faixa deixa de ser desenhada — junto com o acesso que ela anunciava.
 */
export function FaixaDeSuporte() {
  const { igreja } = useSessao();

  const { data } = useQuery({
    /*
     * A igreja entra na chave: adotar um acesso de suporte troca a igreja da
     * sessão, e é isso que faz a pergunta ser refeita. Sem ela, a resposta
     * "não há acesso nenhum" ficaria em cache justamente no momento em que
     * passa a haver um.
     */
    queryKey: ['acesso-de-suporte', igreja?.id ?? null],
    queryFn: () => api<FaixaDeImpersonacao | null>('/me/support-access'),
    // O prazo é curto e a faixa precisa sumir quando ele acabar.
    refetchInterval: 60_000,
    retry: false,
  });

  if (!data) return null;

  return (
    <div
      data-testid="faixa-de-suporte"
      role="status"
      /*
       * O par fundo-e-texto do sistema, e não branco sobre vermelho: o vermelho
       * do tema escuro é claro, e texto branco sobre ele fica ilegível. Este par
       * é o mesmo dos selos de recusa, e o contraste vale nos dois temas.
       */
      className="sticky top-0 z-40 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 border-b border-[var(--status-recusado)] bg-[var(--status-recusado-fundo)] px-4 py-2 text-center text-xs font-medium text-[var(--status-recusado)]"
    >
      <ShieldAlert aria-hidden className="size-4 shrink-0" />
      <span>
        Você está em <strong>{data.igreja}</strong> como suporte da El Shaday, só de leitura.
      </span>
      <span>
        Motivo: {data.motivo} · até{' '}
        {new Date(data.expiraEm).toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </span>
    </div>
  );
}
